import { useMemo, useState } from "react";
import FadeIn, { SectionHeading } from "./FadeIn";
import BuyingScale from "./BuyingScale";
import AnimatedNumber from "./motion/AnimatedNumber";
import { Slider } from "@/components/ui/slider";
import { monthlyPayment, fmt } from "./Hero";

const CREDIT_OPTIONS = [
  { label: "Excellent (760+)", rate: 4.89 },
  { label: "Good (680–759)", rate: 5.24 },
  { label: "Fair (600–679)", rate: 5.89 },
  { label: "Below 600", rate: 6.74 },
];

const PROPERTY_OPTIONS = [
  { label: "Detached", costs: 550 },
  { label: "Townhome", costs: 500 },
  { label: "Condo", costs: 800 },
];

function Field({ label, display, value, min, max, step, onChange, testId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2.5">
        <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50">{label}</label>
        <span data-testid={`${testId}-value`} className="font-serif text-lg text-champagne">{display}</span>
      </div>
      <Slider
        data-testid={testId}
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
        className="[&_[role=slider]]:bg-gold [&_[role=slider]]:border-champagne [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5"
      />
    </div>
  );
}

export default function BuyingPower({ onLead }) {
  const [income, setIncome] = useState(140000);
  const [debts, setDebts] = useState(600);
  const [down, setDown] = useState(150000);
  const [credit, setCredit] = useState(CREDIT_OPTIONS[1]);
  const [property, setProperty] = useState(PROPERTY_OPTIONS[0]);

  const { low, high } = useMemo(() => {
    const gross = income / 12;
    const available = Math.max(
      Math.min(gross * 0.39 - property.costs, gross * 0.44 - debts - property.costs),
      0
    );
    const r = credit.rate / 100 / 12;
    const n = 25 * 12;
    const maxMortgage = r > 0 ? (available * (1 - Math.pow(1 + r, -n))) / r : available * n;
    const power = maxMortgage + down;
    return { low: power * 0.94, high: power * 1.05 };
  }, [income, debts, down, credit, property]);

  return (
    <section id="buying-power" data-testid="buying-power-section" className="py-24 lg:py-36 bg-charcoal/95 relative overflow-hidden">
      <div className="absolute -top-40 right-0 w-[520px] h-[520px] rounded-full bg-gold/[0.045] blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          label="Mortgage Buying Power"
          title="Discover Your Buying Power"
          sub="A private-banking style estimate of what you could comfortably finance — before you ever speak to a lender."
        />

        <div className="mt-16 grid lg:grid-cols-12 gap-8 items-stretch">
          <FadeIn className="lg:col-span-7">
            <div className="glass-panel rounded-md p-8 lg:p-12 h-full">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                <Field label="Annual Income" value={income} display={`$${fmt(income)}`} min={40000} max={600000} step={5000} onChange={setIncome} testId="bp-annual-income" />
                <Field label="Monthly Debts" value={debts} display={`$${fmt(debts)}`} min={0} max={5000} step={50} onChange={setDebts} testId="bp-monthly-debts" />
                <Field label="Down Payment" value={down} display={`$${fmt(down)}`} min={10000} max={1000000} step={5000} onChange={setDown} testId="bp-down-payment" />
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 block mb-3">Credit Score Range</label>
                  <div className="flex flex-wrap gap-2" data-testid="bp-credit-score">
                    {CREDIT_OPTIONS.map((c) => (
                      <button
                        key={c.label}
                        data-testid={`bp-credit-${c.label.split(" ")[0].toLowerCase()}`}
                        onClick={() => setCredit(c)}
                        className={`px-3.5 py-2 text-[11px] tracking-wide rounded-sm border transition-colors duration-300 ${
                          credit.label === c.label
                            ? "border-gold/70 text-gold bg-gold/10"
                            : "border-white/15 text-ivory/55 hover:border-gold/40 hover:text-ivory"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50 block mb-3">Property Type</label>
                  <div className="flex flex-wrap gap-2" data-testid="bp-property-type">
                    {PROPERTY_OPTIONS.map((p) => (
                      <button
                        key={p.label}
                        data-testid={`bp-property-${p.label.toLowerCase()}`}
                        onClick={() => setProperty(p)}
                        className={`px-5 py-2 text-[11px] tracking-wide rounded-sm border transition-colors duration-300 ${
                          property.label === p.label
                            ? "border-gold/70 text-gold bg-gold/10"
                            : "border-white/15 text-ivory/55 hover:border-gold/40 hover:text-ivory"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="relative h-full rounded-md border border-gold/25 bg-gradient-to-b from-charcoal-700 to-charcoal-800 p-8 lg:p-12 flex flex-col justify-between overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
              <div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-ivory/50 mb-6">
                  Your estimated buying power
                </p>
                <p data-testid="bp-result" className="font-serif text-4xl lg:text-[44px] leading-tight">
                  <AnimatedNumber value={low} className="gold-text-gradient" /> – <AnimatedNumber value={high} className="gold-text-gradient" />
                </p>
                <p className="mt-6 text-sm text-ivory/50 font-light leading-relaxed">
                  Based on a {credit.label.toLowerCase()} credit profile at an estimated {credit.rate.toFixed(2)}% rate, 25-year amortization.
                </p>
              </div>
              <div className="mt-10">
                <button
                  data-testid="bp-get-strategy-btn"
                  onClick={() => onLead("pre-approval")}
                  className="w-full px-6 py-4 text-xs tracking-[0.2em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm"
                >
                  GET MY PERSONALIZED STRATEGY
                </button>
                <p className="mt-5 text-[11px] text-ivory/35 leading-relaxed">
                  Estimates are for illustration only and do not constitute a mortgage approval, rate hold or guarantee. Your actual qualification is confirmed through a full application.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
        <FadeIn>
          <BuyingScale estimateHigh={high} />
        </FadeIn>
      </div>
    </section>
  );
}
